import React, { useState, useEffect } from "react";
import { IoSearchOutline } from "react-icons/io5";
import { ProfilePicture } from "./CommonComponents";
import { useNavigate } from "react-router-dom";
import { useChat } from "../context/ChatContext";
import { useSession } from "../SessionContext";
import supabase from "../utils/supabase";
import getAvatarUrl from "../utils/dicebear";
import { format } from "timeago.js";

function Chats() {
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const { chats, loadUserChats } = useChat();
  const session = useSession();
  const [chatMessages, setChatMessages] = useState({});
  const [userStatuses, setUserStatuses] = useState({});
  const [unreadCounts, setUnreadCounts] = useState({});

  useEffect(() => {
    if (session?.user?.id) {
      loadUserChats(session.user.id);

      // Subscribe to new messages
      const subscription = supabase
        .channel("public:messages")
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "messages",
          },
          (payload) => {
            // Update the chatMessages state when a new message arrives
            setChatMessages((prevMessages) => ({
              ...prevMessages,
              [payload.new.chat_id]: payload.new,
            }));
          }
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [session]);

  useEffect(() => {
    // Load last messages for each chat
    const loadLastMessages = async () => {
      const messages = {};
      for (const chat of chats) {
        const { data } = await supabase
          .from("messages")
          .select("*")
          .eq("chat_id", chat.id)
          .order("created_at", { ascending: false })
          .limit(1);
        messages[chat.id] = data?.[0];
      }
      setChatMessages(messages);
    };

    if (chats.length > 0) {
      loadLastMessages();
    }
  }, [chats]);

  useEffect(() => {
    if (session?.user?.id) {
      // Load unread message counts
      const loadUnreadCounts = async () => {
        // Using raw count query instead of groupBy
        const { data } = await supabase
          .from("messages")
          .select(
            `
            chat_id,
            count: count(*)::integer
          `,
            { count: "exact" }
          )
          .eq("receiver_id", session.user.id)
          .eq("is_read", false)
          .or("chat_id.neq.0")
          .group("chat_id");

        const counts = {};
        data?.forEach((item) => {
          counts[item.chat_id] = item.count;
        });
        setUnreadCounts(counts);
      };

      // Load user statuses
      const loadUserStatuses = async () => {
        const { data } = await supabase.from("user_status").select("*");

        const statusMap = {};
        data?.forEach((status) => {
          statusMap[status.user_id] = status;
        });
        setUserStatuses(statusMap);
      };

      loadUnreadCounts();
      loadUserStatuses();

      // Subscribe to both messages and user status changes
      const messagesChannel = supabase.channel("updates");
      messagesChannel
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "messages" },
          loadUnreadCounts
        )
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "user_status" },
          loadUserStatuses
        )
        .subscribe();

      return () => messagesChannel.unsubscribe();
    }
  }, [session?.user?.id]);

  const getOtherUser = (chat) => {
    const isUser1 = chat.user1 === session?.user?.id;
    return isUser1 ? chat.user2_info : chat.user1_info;
  };

  const filteredChats = chats
    .filter((chat) => {
      const otherUser = getOtherUser(chat);
      return otherUser.name.toLowerCase().includes(searchQuery.toLowerCase());
    })
    .sort((a, b) => {
      // Sort by latest message timestamp
      const lastMessageA = chatMessages[a.id];
      const lastMessageB = chatMessages[b.id];

      if (!lastMessageA) return 1; // If no message in chat A, put it at the end
      if (!lastMessageB) return -1; // If no message in chat B, put it at the end

      return (
        new Date(lastMessageB.created_at) - new Date(lastMessageA.created_at)
      );
    });

  const formatChatTime = (timestamp) => {
    if (!timestamp) return "";
    // Parse the UTC timestamp and convert to local time
    const date = new Date(timestamp + "Z"); // Add 'Z' to treat as UTC
    return format(date);
  };

  return (
    <div className="page nav-exists bg-gray-50">
      <div className="max-w-2xl mx-auto p-4">
        {/* Search Bar */}
        <div className="sticky top-0 bg-white rounded-lg shadow-sm mb-4 p-4">
          <div className="relative">
            <IoSearchOutline
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Search chats..."
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Chat List */}
        <div className="space-y-2">
          {filteredChats.map((chat) => {
            const otherUser = getOtherUser(chat);
            console.log(otherUser);
            const lastMessage = chatMessages[chat.id];
            const unreadCount = unreadCounts[chat.id] || 0;

            return (
              <div
                key={chat.id}
                className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/chat/${chat.id}`)}
              >
                <div className="flex items-center space-x-4">
                  <ProfilePicture
                    src={getAvatarUrl(otherUser.name)}
                    size={50}
                  />
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold flex items-center">
                          {otherUser.name}
                          <span
                            className={`ml-2 h-2 w-2 rounded-full ${
                              userStatuses[otherUser.id]?.is_online
                                ? "bg-green-500"
                                : "bg-gray-400"
                            }`}
                          ></span>
                        </h3>
                        <p className="text-xs text-blue-600">
                          {otherUser.role}
                        </p>
                      </div>
                      {lastMessage && (
                        <span className="text-xs text-gray-500">
                          {formatChatTime(lastMessage.created_at)}
                        </span>
                      )}
                    </div>
                    {lastMessage && (
                      <p className="text-sm text-gray-600 truncate mt-1">
                        {lastMessage.message}
                      </p>
                    )}
                    {unreadCount > 0 && (
                      <span className="bg-blue-500 text-white rounded-full px-2 py-1 text-xs">
                        {unreadCount}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default Chats;
